<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import UploadPicker from '@/components/upload/UploadPicker.vue'
import type { PickedUpload } from '@/components/upload/UploadPicker.vue'
import FormField from '@/components/input/FormField.vue'
import NotificationSaved from '@/components/notifications/NotificationSaved.vue'
import {
  FileUploadCheckDeleteMutation,
  FileUploadCheckDeleteMutationVariables,
  FileUploadCheckSetMutation,
  FileUploadCheckSetMutationVariables,
} from '@/generated/graphql'

const props = defineProps<{
  value?: PickedUpload
  required?: boolean
  checkConfigurationId: string
  poNumber?: string
  serialNumber?: string
  allowedMimeTypes?: string[] | null
}>()

const { t } = useI18n()

const pickerOpen = ref(false)
const notifySaved = ref(false)

const setMutation = useMutation<FileUploadCheckSetMutation, FileUploadCheckSetMutationVariables>(
  gql`
    mutation FileUploadCheckSet($command: SetFileUploadCheckCommand!) {
      shopFloor {
        setFileUploadCheck(command: $command) {
          id
        }
      }
    }
  `,
  { refetchQueries: ['CheckData'], awaitRefetchQueries: true },
)

const deleteMutation = useMutation<
  FileUploadCheckDeleteMutation,
  FileUploadCheckDeleteMutationVariables
>(
  gql`
    mutation FileUploadCheckDelete($command: DeleteCheckCommand!) {
      shopFloor {
        deleteCheck(command: $command)
      }
    }
  `,
  { refetchQueries: ['CheckData'], awaitRefetchQueries: true },
)

function baseCommand() {
  if (props.serialNumber == undefined || !props.poNumber) {
    return undefined
  }
  return {
    checkConfigurationId: props.checkConfigurationId,
    poNumber: props.poNumber,
    serialNumber: props.serialNumber,
  }
}

function onPickOne(upload: PickedUpload) {
  const command = baseCommand()
  if (!command) {
    return
  }
  setMutation.mutate({ command: { ...command, uploadId: upload.id } }).then(() => {
    notifySaved.value = true
  })
}

function onDelete() {
  const command = baseCommand()
  if (!command) {
    return
  }
  deleteMutation.mutate({ command }).then(() => {
    notifySaved.value = true
  })
}
</script>

<template>
  <form-field :label="props.serialNumber ?? ''" :required="props.required" class="mb-2">
    <div class="mt-2" v-bind="$attrs">
      <v-chip
        v-if="props.value"
        label
        closable
        prepend-icon="attach_file"
        class="file-chip"
        :title="props.value.name"
        :disabled="deleteMutation.loading.value"
        @click:close="onDelete"
      >
        <span class="text-truncate">{{ props.value.name }}</span>
      </v-chip>
      <v-btn
        v-else
        variant="tonal"
        size="small"
        prepend-icon="upload_file"
        :loading="setMutation.loading.value"
        @click="pickerOpen = true"
      >
        {{ t('entity.check.uploadButton') }}
      </v-btn>
      <upload-picker
        v-model="pickerOpen"
        :mime-types="props.allowedMimeTypes ?? []"
        pick-one
        @pick-one="onPickOne"
      />
    </div>
  </form-field>
  <notification-saved v-model="notifySaved" />
</template>

<style scoped lang="scss">
.file-chip.v-chip {
  max-width: 100%;

  :deep(.v-chip__content) {
    min-width: 0;
  }

  :deep(.v-chip__close) {
    flex-shrink: 0;
    margin-inline-start: 8px;
  }
}
</style>
